/**
 * Resume Scorecard Modal
 *
 * Deterministic readout of how the resume reads — headlined by the
 * "Authenticity" score (how human vs. AI-generated the writing sounds). Runs on
 * the in-memory resume (including unsaved edits). Everything is rule-based and
 * free: the Authenticity/Quantification/Completeness dimensions are computed in
 * the browser, and Correctness comes from the server's deterministic lint pass
 * (`mode: 'lint'` on the review endpoint — no LLM, no usage meter).
 *
 * Available to all users (trial/normal/active) — unlike the AI Review feature,
 * this is read-only guidance, not an AI-assisted rewrite.
 */
import { useRef, useMemo, useState, useEffect } from 'react';
import { X, Gauge, ChevronDown, ChevronRight, Check } from 'lucide-react';
import { useEscapeClose } from '../hooks/useEscapeClose';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { api } from '../services/api';
import type { ResumeJSON } from '../types';
import { buildScorecard, scoreColor, getTextAtPath, WEIGHTS, DEFAULT_RULE_OF_THREE_TOLERANCE, DEFAULT_QUANTIFICATION_TARGET, type DimensionScore, type Finding, type FindingPath, type LintFinding, type Severity } from '../utils/scorecard';
import { BulletTextWithLineBreaks } from './BulletTextWithLineBreaks';

interface ScorecardModalProps {
  isOpen: boolean;
  onClose: () => void;
  resume: ResumeJSON;
  /** Name of the resume record being edited — only used to address the lint
   *  endpoint (`POST /resumes/{resumename}/review`). The live `resume` above is
   *  what actually gets linted, so a missing name still works: we fall back to
   *  the candidate name. Pass it when available so server logs are accurate. */
  resumename?: string;
  /** Persist an edited item back to the resume (update state + save). */
  onSaveItem?: (path: FindingPath, newText: string) => Promise<void> | void;
  /** Persist the set of manually-resolved finding ids onto the resume so a
   *  dismissal survives across sessions (saved with the resume). */
  onResolvedChange?: (resolvedIds: string[]) => void;
}

// Tooltip shown on every "Rule-of-three list" finding so users understand the
// (narrow) logic and that they can permanently dismiss intentional ones.
const RULE_OF_THREE_HELP =
  'Flagged as a rule-of-three: three single-word items in a list ' +
  '(e.g. "fast, reliable, and scalable") — a cadence that reads as AI-written. ' +
  'Lists of four or more items, and multi-word items, are NOT flagged. ' +
  'If this phrasing is intentional, click "Mark resolved" below to dismiss it — ' +
  'it stays dismissed once you save your resume.';

const SEV_DOT: Record<Severity, string> = { high: 'bg-red-500', med: 'bg-amber-500', low: 'bg-gray-400' };

function ScoreRing({ score }: { score: number }) {
  const c = scoreColor(score);
  const r = 34, circ = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, score)) / 100) * circ;
  const stroke = score >= 85 ? '#22c55e' : score >= 70 ? '#f59e0b' : '#ef4444';
  return (
    <div className="relative w-24 h-24 flex-shrink-0">
      <svg className="w-24 h-24 -rotate-90" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r={r} fill="none" stroke="#e5e7eb" strokeWidth="7" />
        <circle cx="40" cy="40" r={r} fill="none" stroke={stroke} strokeWidth="7" strokeLinecap="round" strokeDasharray={`${dash} ${circ}`} />
      </svg>
      <div className={`absolute inset-0 flex flex-col items-center justify-center ${c.text}`}>
        <span className="text-2xl font-bold leading-none">{score}</span>
        <span className="text-[10px] text-gray-400">/ 100</span>
      </div>
    </div>
  );
}

interface FindingRowProps {
  finding: Finding;
  resume: ResumeJSON;
  resolved: boolean;
  onToggleResolved: () => void;
  onSaveItem?: (path: FindingPath, newText: string) => Promise<void> | void;
}

function FindingRow({ finding: f, resume, resolved, onToggleResolved, onSaveItem }: FindingRowProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const editable = !!f.path && !!onSaveItem;
  // Experience bullets get the PDF-aware editor (line-break ↵ markers, orphan
  // highlighting) — the same control used in Edit Resume. Other sections use
  // the plain textarea.
  const isBullet = f.path?.kind === 'bullet';
  const liveText = f.path ? getTextAtPath(resume, f.path) : '';
  const value = draft ?? liveText;
  const dirty = draft !== null && draft !== liveText;

  async function handleItemSave() {
    if (!f.path || !onSaveItem || !dirty) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSaveItem(f.path, value);
      setDraft(null);
    } catch {
      setSaveError('Save failed — please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <li className="rounded border border-gray-200/80 bg-white/60">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start gap-2 text-xs text-left px-2 py-1.5 hover:bg-gray-50 rounded"
      >
        {resolved ? (
          <Check className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" aria-label="Resolved" />
        ) : (
          <span className={`w-2 h-2 rounded-full mt-1 ml-0.5 mr-0.5 flex-shrink-0 ${SEV_DOT[f.severity]}`} />
        )}
        <span className="min-w-0 flex-1">
          <span
            className={`${resolved ? 'text-gray-400 line-through' : 'text-gray-700'}${/^Rule-of-three/.test(f.label) ? ' underline decoration-dotted decoration-gray-300 underline-offset-2 cursor-help' : ''}`}
            title={/^Rule-of-three/.test(f.label) ? RULE_OF_THREE_HELP : undefined}
          >{f.label}</span>
          <span className="text-gray-400"> · {f.section}</span>
          {!open && f.detail && <span className="block text-gray-400 italic truncate">“{f.detail}”</span>}
        </span>
        {open
          ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
          : <ChevronRight className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-2 pb-2 space-y-2">
          {editable && isBullet ? (
            // PDF-accurate bullet editor. It renders at the true PDF content
            // width (743px) so each computed line stays on one visual row; the
            // modal (max-w-[860px]) is sized to fit it snugly with a small
            // margin — wide enough to avoid scroll, narrow enough that the
            // editor isn't stranded in dead space. overflow-x-auto only kicks
            // in on very narrow screens — re-wrapping would misrepresent the
            // real PDF line breaks.
            <div className="overflow-x-auto">
              <BulletTextWithLineBreaks text={value} onChange={(t) => setDraft(t)} />
            </div>
          ) : editable ? (
            <textarea
              value={value}
              onChange={(e) => setDraft(e.target.value)}
              rows={Math.min(8, Math.max(3, Math.ceil(value.length / 70)))}
              className="w-full text-xs border border-gray-300 rounded p-2 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y"
              aria-label={`Edit item: ${f.section}`}
            />
          ) : (
            f.detail
              ? <p className="text-xs text-gray-500 italic">“{f.detail}”</p>
              : <p className="text-xs text-gray-400">This finding spans multiple items — edit them in the resume sections directly.</p>
          )}
          {saveError && <p className="text-xs text-red-600">{saveError}</p>}
          <div className="flex items-center gap-2">
            {editable && (
              <button
                type="button"
                onClick={handleItemSave}
                disabled={!dirty || saving}
                className="px-2.5 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-40"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            )}
            <button
              type="button"
              onClick={onToggleResolved}
              className={`px-2.5 py-1 text-xs font-medium rounded border ${
                resolved
                  ? 'text-gray-500 border-gray-300 hover:bg-gray-50'
                  : 'text-green-700 border-green-300 hover:bg-green-50'
              }`}
            >
              {resolved ? 'Mark unresolved' : 'Mark resolved'}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

interface DimensionCardProps {
  dim: DimensionScore;
  resume: ResumeJSON;
  resolvedIds: Set<string>;
  onToggleResolved: (id: string) => void;
  onSaveItem?: (path: FindingPath, newText: string) => Promise<void> | void;
}

function DimensionCard({ dim, resume, resolvedIds, onToggleResolved, onSaveItem }: DimensionCardProps) {
  const [open, setOpen] = useState(dim.key === 'authenticity');
  const c = scoreColor(dim.score);
  const hasFindings = dim.findings.length > 0;
  const unresolvedCount = dim.findings.filter((f) => !resolvedIds.has(f.id!)).length;
  return (
    <div className={`border rounded-lg ${c.bg} border-gray-200`}>
      <div className="p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-base font-semibold text-gray-800">{dim.label}</span>
            {dim.key === 'authenticity' && <span className="text-[10px] uppercase tracking-wide text-gray-400">headline</span>}
          </div>
          <div className="flex flex-col items-end flex-shrink-0 leading-tight">
            <span className={`text-sm font-bold ${c.text}`}>{dim.score === null ? 'N/A' : `${dim.score}/100`}</span>
            <span className="text-[10px] text-gray-400">({Math.round(WEIGHTS[dim.key] * 100)}% of overall score)</span>
          </div>
        </div>
        <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
          <div className={`h-full ${c.bar}`} style={{ width: `${dim.score ?? 0}%` }} />
        </div>
        <p className="text-xs text-gray-600 mt-2">{dim.blurb}</p>
      </div>
      {hasFindings && (
        <div className="border-t border-gray-200/70">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="w-full flex items-center gap-1 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700"
          >
            {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            {dim.findings.length} {dim.findings.length === 1 ? 'item' : 'items'} to look at
            {unresolvedCount < dim.findings.length && (
              <span className="text-green-600">· {dim.findings.length - unresolvedCount} resolved</span>
            )}
          </button>
          {open && (
            <ul className="px-3 pb-3 space-y-1.5 max-h-80 overflow-y-auto">
              {/* No cap — show every finding; the list scrolls (max-h-80). */}
              {dim.findings.map((f) => (
                <FindingRow
                  key={f.id}
                  finding={f}
                  resume={resume}
                  resolved={resolvedIds.has(f.id!)}
                  onToggleResolved={() => onToggleResolved(f.id!)}
                  onSaveItem={onSaveItem}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function ScorecardModal({ isOpen, onClose, resume, resumename, onSaveItem, onResolvedChange }: ScorecardModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useEscapeClose(onClose, isOpen);
  useFocusTrap(modalRef, isOpen);
  // Rule-of-three tolerance is an admin-tweakable global setting (fraction of
  // resume items that may be triple-lists for free). Falls back to the default
  // if the setting is absent or the fetch fails.
  const [r3Tolerance, setR3Tolerance] = useState(DEFAULT_RULE_OF_THREE_TOLERANCE);
  const [quantTarget, setQuantTarget] = useState(DEFAULT_QUANTIFICATION_TARGET);
  useEffect(() => {
    api.getPublicSetting('rule_of_three_tolerance')
      .then((d) => {
        const v = Number(d?.setting_value);
        if (Number.isFinite(v) && v >= 0 && v < 1) setR3Tolerance(v);
      })
      .catch(() => { /* keep default */ });
    api.getPublicSetting('quantification_target_rate')
      .then((d) => {
        const v = Number(d?.setting_value);
        if (Number.isFinite(v) && v > 0 && v <= 1) setQuantTarget(v);
      })
      .catch(() => { /* keep default */ });
  }, []);
  // Correctness dimension: deterministic lint findings from the server
  // (spelling, doubled words, tense drift, duplicate skills…). The LIVE
  // in-memory resume is sent, so unsaved edits are included. Free for every
  // user — no LLM, no usage meter. `null` = still loading; on failure the
  // dimension shows an error blurb with score null and the overall score
  // simply excludes it.
  const [lintFindings, setLintFindings] = useState<LintFinding[] | null>(null);
  const [lintError, setLintError] = useState(false);
  // Read the resume at fetch time without re-firing the request on every
  // keystroke: the scorecard lints what's on screen when it opens.
  const resumeRef = useRef(resume);
  resumeRef.current = resume;
  // The resumename only addresses the endpoint (the body carries the resume
  // that actually gets linted), so when the prop isn't supplied a sanitized
  // candidate name is a safe stand-in — path-unsafe characters are stripped so
  // API Gateway can't reject the request.
  const lintName = resumename || resume.name?.replace(/[^\w .-]/g, '').trim() || 'resume';
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLintFindings(null);
    setLintError(false);
    api.lintResume(lintName, resumeRef.current)
      .then((d) => { if (!cancelled) { setLintFindings(d?.findings || []); setLintError(false); } })
      .catch(() => { if (!cancelled) { setLintFindings(null); setLintError(true); } });
    return () => { cancelled = true; };
  }, [isOpen, lintName]);

  const card = useMemo(
    () => buildScorecard(resume, {
      ruleOfThreeTolerance: r3Tolerance,
      quantificationTarget: quantTarget,
      lintFindings,
      lintError,
    }),
    [resume, r3Tolerance, quantTarget, lintFindings, lintError]);
  // Manually-resolved finding IDs. Seeded from the resume (persisted via
  // onResolvedChange → saved with the resume) so a dismissal survives across
  // sessions, not just while the editor is open.
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(() => new Set(resume.scorecard_resolved || []));
  const toggleResolved = (id: string) => setResolvedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    onResolvedChange?.(Array.from(next));
    return next;
  });

  if (!isOpen) return null;
  const oc = scoreColor(card.overall);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-label="Resume Scorecard">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-[860px] max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Gauge className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Resume Scorecard</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          <div className={`flex items-center gap-4 p-4 rounded-lg ${oc.bg} mb-4`}>
            <ScoreRing score={card.overall} />
            <div>
              <div className="text-sm font-semibold text-gray-800">Overall readiness</div>
              <p className="text-xs text-gray-600 mt-1">
                A deterministic read of how your resume comes across. The <span className="font-medium">Authenticity</span> score
                estimates how human (vs. AI-generated) the writing sounds — higher is better.
              </p>
            </div>
          </div>

          {/* Severity legend — maps the finding dots in each section below.
              Uses the same SEV_DOT colors as FindingRow so it stays in sync. */}
          <div className="flex items-center justify-between flex-wrap gap-x-3 gap-y-1 px-3 py-2 mb-4 rounded-md bg-gray-50 border border-gray-200 text-[11px] text-gray-600">
            <span className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${SEV_DOT.high}`} /> High — fix first
            </span>
            <span className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${SEV_DOT.med}`} /> Medium — worth revising
            </span>
            <span className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${SEV_DOT.low}`} /> Low — minor / optional
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="w-3 h-3 text-green-600" /> Resolved
            </span>
          </div>

          <div className="space-y-3">
            {card.dimensions.map((d) => (
              <DimensionCard
                key={d.key}
                dim={d}
                resume={resume}
                resolvedIds={resolvedIds}
                onToggleResolved={toggleResolved}
                onSaveItem={onSaveItem}
              />
            ))}
          </div>

          <p className="text-[11px] text-gray-400 mt-4 leading-relaxed">
            This is a guide, not a gate — it flags patterns, it doesn’t rewrite. Numbers and ambiguous words are scored
            conservatively, so use your judgment. For AI-assisted fixes, run <span className="italic">Review Resume and Generate Recommendations</span>.
          </p>
        </div>
      </div>
    </div>
  );
}
